export type Mention = {
  id: string;
  label: string;
};

export type MentionTarget = {
  id: string;
  handle: string;
  label: string;
  title?: string;
  href?: string;
};

export type ActiveMentionQuery = {
  query: string;
  start: number;
};

const MENTION_LINK = /\[@([^\]\n]+)\]\(mention:([A-Za-z0-9_-]+)\)/g;
const MENTION_LINK_AT_START = /^\[@([^\]\n]+)\]\(mention:([A-Za-z0-9_-]+)\)/;
const CODE_FENCE = /```[\s\S]*?```/g;
const INLINE_CODE = /`[^`\n]*`/g;
const QUERY_CHAR = /[a-zA-Z0-9._-]/;
const WORD_START_BEFORE = /[\s([{]/;

export const mentionMarkup = (id: string, label: string): string => `[@${label}](mention:${id})`;

export const mentionAtStart = (value: string): { mention: Mention; length: number } | null => {
  const found = MENTION_LINK_AT_START.exec(value);
  return found ? { mention: { id: found[2] ?? "", label: found[1] ?? "" }, length: found[0].length } : null;
};

export const mentionedIds = (source: string): string[] => [...new Set([...source.matchAll(MENTION_LINK)].map((match) => match[2] ?? ""))].filter(Boolean);

export const addedMentionIds = (from: string, to: string): string[] => {
  const before = new Set(mentionedIds(from));
  return mentionedIds(to).filter((id) => !before.has(id));
};

export const replaceMentions = (source: string, replace: (mention: Mention, index: number) => string): string =>
  source.replace(MENTION_LINK, (_match, label: string, id: string, index: number) => replace({ id, label }, index));

const findRanges = (value: string, pattern: RegExp): Array<[number, number]> => {
  const ranges: Array<[number, number]> = [];
  for (const match of value.matchAll(pattern)) {
    const start = match.index ?? 0;
    ranges.push([start, start + match[0].length]);
  }
  return ranges;
};

export const isInsideCode = (value: string, index: number): boolean => {
  const ranges = [...findRanges(value, CODE_FENCE), ...findRanges(value, INLINE_CODE)];
  return ranges.some(([start, end]) => index > start && index < end);
};

export const activeMentionQuery = (value: string, caret: number): ActiveMentionQuery | null => {
  if (caret <= 0 || caret > value.length || isInsideCode(value, caret)) {
    return null;
  }

  let index = caret;
  while (index > 0 && QUERY_CHAR.test(value[index - 1] ?? "")) {
    index--;
  }

  if (value[index - 1] !== "@") {
    return null;
  }

  const atIndex = index - 1;
  const before = value[atIndex - 1];
  if (before !== undefined && !WORD_START_BEFORE.test(before)) {
    return null;
  }

  return { query: value.slice(index, caret), start: atIndex };
};

export const matchMentionCandidates = (query: string, targets: MentionTarget[]): MentionTarget[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return targets;
  }
  return targets.filter(
    (target) =>
      target.handle.toLowerCase().includes(normalized) ||
      target.label.toLowerCase().includes(normalized) ||
      (target.title ?? "").toLowerCase().includes(normalized),
  );
};
